import { fraxlendPairsFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { fraxlendLenderKey } from '../../public-data/fraxlend/convertPublic'
import { FRAXLEND_CALLS_PER_PAIR } from './userCallBuild'
import { rebuildFraxlendData, usesFraxlendLens } from './lens'

/** Per-position Fraxlend detail attached to the debt row (raw strings). */
export interface FraxlendPositionInfo {
  /** Debt in SHARES, as stored. The repay encoder takes SHARES, so this is the
   *  number it needs — `debt` on the row is the amount equivalent. */
  borrowShares: string
  /** Post-accrual `totalBorrow` vault account, so a caller can redo the
   *  share<->amount conversion at a different rounding if it needs to. */
  totalBorrowAmount: string
  totalBorrowShares: string
  /** The user's LENDER-side position on the same pair, in fToken shares and in
   *  underlying. Fraxlend's pair IS the ERC-4626 vault, so a user can be a
   *  lender and a borrower on one contract at once — these are independent of
   *  the collateral/debt rows and must never be netted against them. */
  fTokenShares: string
  fTokenAssets: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Fraxlend's `VaultAccount.toAmount`, reproduced exactly.
 *
 * The round-up branch is NOT the usual `(a*b + d - 1) / d` — Fraxlend
 * re-multiplies and compares against the ORIGINAL shares, dividing by
 * `total.amount` rather than `total.shares`:
 *
 *   amount = shares * total.amount / total.shares
 *   if (roundUp && (amount * total.shares) / total.amount < shares) amount += 1
 *
 * Reproducing it verbatim matters because a debt quoted one wei light leaves
 * dust behind on a "full" close, and dust debt on Fraxlend keeps the position
 * open and the collateral locked.
 */
export function fraxlendToAmount(
  shares: bigint,
  totalAmount: bigint,
  totalShares: bigint,
  roundUp: boolean,
): bigint {
  if (totalShares === 0n) return shares
  let amount = (shares * totalAmount) / totalShares
  if (
    roundUp &&
    totalAmount > 0n &&
    (amount * totalShares) / totalAmount < shares
  ) {
    amount = amount + 1n
  }
  return amount
}

/**
 * Returns a [converter, expectedCallCount] tuple for Fraxlend user data.
 *
 * Layout mirrors `buildFraxlendUserCall` (deterministic from the curated
 * allowlist — no discovery). Emits one `UserData` per pair key
 * `FRAXLEND_<chainId>_<PAIR>`, each with a single sub-account `'0'`
 * (positions are address-keyed: at most ONE loan per user per pair):
 * a collateral row and a debt row.
 *
 * Debt is converted from shares LOCALLY using the post-accrual `totalBorrow`
 * returned by `previewAddInterest` in the same round — see `userCallBuild` for
 * why that beats a second `toBorrowAmount` round, and `fraxlendToAmount` for
 * why the rounding is reproduced rather than approximated.
 */
export const getFraxlendUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const pairs = fraxlendPairsFor(lender, chainId)
  const expected = pairs.length * FRAXLEND_CALLS_PER_PAIR
  const viaLens = expected > 0 && usesFraxlendLens(chainId)

  return [
    (raw: any[]) => {
      if (expected === 0) return undefined
      if (!raw) return undefined
      if (viaLens && raw.length !== 1) return undefined

      // One blob in, the walk's own flat array out — the cursor arithmetic AND
      // the share conversion below run identically on both paths. A declined
      // rebuild is an incomplete read, never an empty position.
      const data = viaLens ? rebuildFraxlendData(raw[0], pairs.length) : raw
      if (!data || data.length < expected) return undefined

      const out: { [lender: string]: UserData } = {}

      pairs.forEach((pair, i) => {
        const base = i * FRAXLEND_CALLS_PER_PAIR
        const collRaw = data[base]
        const sharesRaw = data[base + 1]
        const fTokenRaw = data[base + 2]
        const previewRaw = data[base + 3]

        // A failed collateral/shares read is NOT an empty position — skip the
        // pair rather than reporting the user as debt-free, which would render
        // an open loan as closed.
        if (isFailedCall(collRaw) || isFailedCall(sharesRaw)) return

        const collateral = big(collRaw)
        const borrowShares = big(sharesRaw)
        const fTokenShares = isFailedCall(fTokenRaw) ? 0n : big(fTokenRaw)
        if (collateral === 0n && borrowShares === 0n && fTokenShares === 0n)
          return

        // previewAddInterest -> (…, …, …, rateInfo, totalAsset, totalBorrow)
        let totalBorrowAmount = 0n
        let totalBorrowShares = 0n
        let totalAssetAmount = 0n
        let totalAssetShares = 0n
        if (!isFailedCall(previewRaw) && previewRaw) {
          const ta = (previewRaw as any)[4]
          const tb = (previewRaw as any)[5]
          totalAssetAmount = big(ta?.amount ?? ta?.[0])
          totalAssetShares = big(ta?.shares ?? ta?.[1])
          totalBorrowAmount = big(tb?.amount ?? tb?.[0])
          totalBorrowShares = big(tb?.shares ?? tb?.[1])
        }

        // Round UP for debt (the direction the protocol charges) and DOWN for
        // the lender position (the direction it pays out).
        const debt = fraxlendToAmount(
          borrowShares,
          totalBorrowAmount,
          totalBorrowShares,
          true,
        )
        const fTokenAssets = fraxlendToAmount(
          fTokenShares,
          totalAssetAmount,
          totalAssetShares,
          false,
        )

        const lenderKey = fraxlendLenderKey(lender, chainId, pair.address)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        // The public converter published exactly two rows per pair, keyed by
        // the asset and the collateral. Recover both from the meta map rather
        // than re-reading addresses: it is the same source of truth.
        //
        // The flags live under `flags`, NOT at the row root — reading
        // `metaMap[u].collateralActive` compiles under the bundler but is
        // always `undefined`, which would silently pick the wrong row (or
        // neither) and drop every position.
        //
        // The two legs are asymmetric by construction, which is what makes
        // this unambiguous: the collateral leg is the only one with
        // `collateralActive`, and the asset leg the only one with
        // `borrowingEnabled`.
        const uids = Object.keys(metaMap)
        const collUid = uids.find(
          (u) => metaMap[u]?.flags?.collateralActive === true,
        )
        const loanUid = uids.find(
          (u) => metaMap[u]?.flags?.borrowingEnabled === true,
        )
        if (!collUid || !loanUid || collUid === loanUid) return

        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collAddr = collMeta?.underlying?.toLowerCase() ?? ''
        const loanAddr = loanMeta?.underlying?.toLowerCase() ?? ''
        const collDecimals = collMeta?.asset?.decimals ?? 18
        const loanDecimals = loanMeta?.asset?.decimals ?? 18

        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const fraxlendInfo: FraxlendPositionInfo = {
          borrowShares: borrowShares.toString(),
          totalBorrowAmount: totalBorrowAmount.toString(),
          totalBorrowShares: totalBorrowShares.toString(),
          fTokenShares: fTokenShares.toString(),
          fTokenAssets: fTokenAssets.toString(),
        }

        const collStr = parseRawAmount(collateral.toString(), collDecimals)
        const collNum = Number(collStr)
        const debtStr = parseRawAmount(debt.toString(), loanDecimals)
        const debtNum = Number(debtStr)
        // The lender-side position lives on the ASSET row's deposits.
        const supplyStr = parseRawAmount(fTokenAssets.toString(), loanDecimals)
        const supplyNum = Number(supplyStr)

        const lendingPositions: Record<string, any> = {
          '0': {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: loanAddr,
              deposits: supplyStr,
              debt: debtStr,
              debtStable: '0',
              depositsUSD: supplyNum * loanDisplay,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: supplyNum * loanOracle,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              // The asset leg is never collateral in its own pair.
              collateralEnabled: false,
              claimableRewards: 0,
              fraxlendInfo,
            },
          },
        }
        const modes: Record<string, number> = { '0': 0 }
        const hist = {
          '0': {
            totalDeposits24h: collNum * collHist + supplyNum * loanHist,
            totalDebt24h: debtNum * loanHist,
          },
        }

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      return Object.keys(out).length > 0 ? out : undefined
    },
    // One blob on the lens path, the whole per-pair walk otherwise. `expected`
    // stays the SLOT count the cursor arithmetic above is written against.
    viaLens ? 1 : expected,
  ]
}
